import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Button from "../../Components/Button";
import Price from "../../Components/Price";
import { CheckIcon, CloseIcon } from "../../Components/Icons";
import { getCookie } from "../../util/cookies";
import { opmStorage, Keys } from "../../util/opmStorage";
import { cancelWebPayment, getWebPaymentStatus } from "../../services/ApiCalls";
import { clearPendingWebPayment, readPendingWebPayment } from "../../util/webPayment";
import useTableBill from "../../hooks/useTableBill";
import { notifySessionExpired, SessionEndReason } from "../../util/session";

const POLL_EVERY_MS = 1000;
const POLL_FOR_MS = 60000;
// The wait is usually shorter than this, so most guests see no spinner at all.
const SPINNER_AFTER_MS = 1500;
const WALLET_NAMES = { apple_pay: "Apple Pay", google_pay: "Google Pay" };
// Released because the table's bill no longer covers the payment.
const BILL_CHANGED = ["bill_changed", "no_business_day"];

/**
 * The payment this return address is for. The tab kept it before leaving for the payment
 * page; if that was lost (another tab, storage blocked), the token in the address names the
 * restaurant and table — enough to ask the server, which checks the token itself.
 */
const pendingFor = (restaurantId, elementId, uniqueId, token, signature) => {
  const kept = readPendingWebPayment(restaurantId, elementId, uniqueId);
  if (kept) return kept;
  try {
    const claims = JSON.parse(atob(token.replace(/-/g, "+").replace(/_/g, "/")));
    return {
      uniqueId,
      token,
      signature,
      restaurantId: String(claims.r),
      diningSessionId: Number(claims.s),
      base: (Number(claims.a) || 0) / 100,
      tip: (Number(claims.t) || 0) / 100,
    };
  } catch {
    return null;
  }
};

const opmToken = (restaurantId, elementId) =>
  restaurantId && elementId ? opmStorage.getWithTtl(Keys.token(restaurantId, elementId)) : null;

const Screen = ({ icon, iconClass, title, children, actions }) => (
  <div className="h-dvh flex flex-col bg-background">
    <main className="flex-1 overflow-y-auto px-6 pt-16 flex flex-col items-center gap-5 text-center">
      <span className={`w-20 h-20 rounded-full flex items-center justify-center ${iconClass}`}>{icon}</span>
      <h1 className="text-lg font-bold text-dark">{title}</h1>
      {children}
    </main>
    <div
      className="flex-none bg-background shadow-sheet px-6 pt-3 flex flex-col gap-2.5"
      style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}
    >
      {actions}
    </div>
  </div>
);

const NoChargeNote = ({ t }) => (
  <div className="w-full rounded-md bg-success-50 text-success px-4 py-3 flex items-center gap-2.5 text-sm font-medium">
    <CheckIcon size={20} />
    {t("noChargeTaken")}
  </div>
);

/**
 * Where the payment page sends the guest back. It asks the server how the payment went until
 * it is final — the server settles it here too if the acquirer's callback has not — and then
 * moves on to the success screen, or explains why nothing was charged.
 */
const Index = ({ t, navigate }) => {
  const [params] = useSearchParams();
  const uniqueId = params.get("ws") || "";
  const [outcome, setOutcome] = useState({ status: "processing" });
  const [showSpinner, setShowSpinner] = useState(false);
  const pendingRef = useRef(null);
  const { bill } = useTableBill();

  // The table this phone signed in to: where the payment was kept, and whose token pays it.
  const restaurantId = getCookie("opm_restaurant_id");
  const elementId = getCookie("opm_element_id");
  const forget = () => clearPendingWebPayment(restaurantId, elementId);

  useEffect(() => {
    const spinnerTimer = setTimeout(() => setShowSpinner(true), SPINNER_AFTER_MS);
    return () => clearTimeout(spinnerTimer);
  }, []);

  useEffect(() => {
    const pending = pendingFor(restaurantId, elementId, uniqueId, params.get("d") || "", params.get("sig") || "");
    pendingRef.current = pending;
    if (!pending) {
      setOutcome({ status: "failed" });
      return undefined;
    }
    const token = opmToken(pending.restaurantId, elementId);
    let cancelled = false;
    let timer = null;

    if (params.get("canceled") === "1") {
      cancelWebPayment(pending, token)
        .catch(() => null)
        .finally(() => {
          if (cancelled) return;
          forget();
          navigate("/payment", { replace: true });
        });
      return () => {
        cancelled = true;
      };
    }

    const pageFailed = params.get("failed") === "1";
    const deadline = Date.now() + POLL_FOR_MS;
    const poll = async () => {
      let result = null;
      try {
        result = await getWebPaymentStatus(pending, token);
      } catch (err) {
        if (err?.response?.status === 400) result = { status: "failed" };
      }
      if (cancelled) return;
      const status = result?.status;
      if (status === "captured") {
        forget();
        const amount = Number(result.amount) || 0;
        const tip = Number(result.tip_amount) || 0;
        const wallet = WALLET_NAMES[result.payment_method] || t("card");
        navigate("/payment-success", {
          replace: true,
          state: {
            base: amount,
            tip,
            charge: amount + tip,
            walletName: result.card_last4 ? `${wallet} · •••• ${result.card_last4}` : wallet,
          },
        });
        return;
      }
      if (status === "released" && result.reason === "table_closed") {
        // The table closed while the guest paid: the table-closed popup says nothing was charged.
        forget();
        notifySessionExpired(SessionEndReason.TABLE_CLOSED_UNCHARGED);
        return;
      }
      if (status === "released" || status === "failed") {
        forget();
        setOutcome({ status, reason: result.reason });
        return;
      }
      if (status === "open" && pageFailed) {
        setOutcome({ status: "failed" });
        return;
      }
      if (status === "canceled") {
        forget();
        navigate("/payment", { replace: true });
        return;
      }
      if (Date.now() > deadline) {
        setOutcome({ status: "slow" });
        return;
      }
      timer = setTimeout(poll, POLL_EVERY_MS);
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Runs once per return address.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId]);

  if (outcome.status === "released" && BILL_CHANGED.includes(outcome.reason)) {
    return (
      <Screen
        icon={<span className="text-2xl font-bold">↻</span>}
        iconClass="bg-primary-50 text-primary"
        title={t("billChangedTitle")}
        actions={
          <>
            <Button size="lg" fullWidth onClick={() => navigate("/payment", { replace: true })}>
              {t("toUpdatedBill")}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate("/", { replace: true })}>
              {t("backToMenu")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">{t("billChangedBody")}</p>
        <NoChargeNote t={t} />
        {bill?.remaining_amount != null && (
          <div className="w-full rounded-lg border-[1.5px] border-border px-4 py-3 flex items-baseline justify-between font-bold">
            <span>{t("remainingNow")}</span>
            <Price value={Number(bill.remaining_amount) || 0} className="text-lg text-primary" />
          </div>
        )}
      </Screen>
    );
  }

  if (outcome.status === "failed" || outcome.status === "released") {
    return (
      <Screen
        icon={<CloseIcon size={36} />}
        iconClass="bg-danger-50 text-danger"
        title={t("paymentFailedTitle")}
        actions={
          <>
            <Button size="lg" fullWidth onClick={() => navigate("/payment", { replace: true })}>
              {t("tryAgain")}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate("/", { replace: true })}>
              {t("backToMenu")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">{t("paymentFailedBody")}</p>
        <NoChargeNote t={t} />
      </Screen>
    );
  }

  // Still confirming after a minute: say so, and let the guest check again.
  if (outcome.status === "slow") {
    return (
      <Screen
        icon={<span className="w-16 h-16 rounded-full border-[6px] border-primary-50 border-t-primary" aria-hidden="true" />}
        iconClass=""
        title={t("confirmingPayment")}
        actions={
          <Button size="lg" fullWidth onClick={() => window.location.reload()}>
            {t("checkAgain")}
          </Button>
        }
      >
        <p className="text-sm text-muted">{t("confirmingSlow")}</p>
      </Screen>
    );
  }

  // No screen of its own while the payment is confirmed: it usually takes a second or two (the
  // callback reaches the server before the guest is back), and the guest goes straight to the
  // success screen. A spinner shows only if it takes longer.
  return (
    <div className="h-dvh flex items-center justify-center bg-background" aria-busy="true">
      {showSpinner && (
        <span
          className="w-10 h-10 rounded-full border-4 border-primary-50 border-t-primary animate-spin animate-fade-in"
          role="img"
          aria-label={t("confirmingPayment")}
        />
      )}
    </div>
  );
};

export default Index;
